/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback } from "react";

import { useUnityDispatch, useUnitySelector } from "../../../viewModel";
import type { FeatureStage } from "../../../viewModel/featureActivation";
import {
  resetAllFeatureOverrides,
  resetAllStageOverrides,
  selectFeatureOverrideFocusedIndex,
  selectFeatureOverrideRows,
  selectIsFeatureOverrideVisible,
  selectResetFeatureOverridesIndex,
  selectResetStageOverridesIndex,
  selectVideoDebugOverlayControlIndex,
  setFocusedControl,
  setRowOverride,
  setRowStageOverride
} from "../../../viewModel/featureOverride";
import {
  selectIsVideoDebugOverlayPersisted,
  setVideoDebugOverlayEnabled
} from "../../../viewModel/videoDebugOverlay";
import FeatureOverrideScreen from "./FeatureOverrideScreen";

/**
 * Connected container for the debug-only Feature Override screen.
 *
 * A SINGLE instance is mounted globally in App.tsx. It renders `null` until the
 * hidden `123456` code opens it, so it is invisible during normal usage; in a
 * release build it can never open, because the interceptor that dispatches
 * `openFeatureOverride()` is not installed and the thunk guards on
 * `isDevToolsBuild()`.
 *
 * D-pad input is handled by `useFeatureOverrideInput` (a global interceptor
 * installed while open) rather than a screen-scoped modal, so the overlay
 * responds from any context. The pointer handlers here cover browser/webOS.
 */
const FeatureOverrideContainer: FunctionComponent = (): JSX.Element | null => {
  const dispatch = useUnityDispatch();

  const isVisible = useUnitySelector(selectIsFeatureOverrideVisible);
  const rows = useUnitySelector(selectFeatureOverrideRows);
  const focusedIndex = useUnitySelector(selectFeatureOverrideFocusedIndex);
  const resetStageIndex = useUnitySelector(selectResetStageOverridesIndex);
  const resetFeatureIndex = useUnitySelector(selectResetFeatureOverridesIndex);
  const videoDebugOverlayIndex = useUnitySelector(
    selectVideoDebugOverlayControlIndex
  );
  const isVideoDebugOverlayEnabled = useUnitySelector(
    selectIsVideoDebugOverlayPersisted
  );

  const handleFocusControl = useCallback(
    (index: number): void => {
      dispatch(setFocusedControl(index));
    },
    [dispatch]
  );

  const handleSelectValue = useCallback(
    (index: number, value: boolean | null): void => {
      dispatch(setRowOverride({ index, value }));
    },
    [dispatch]
  );

  const handleSelectStage = useCallback(
    (index: number, stage: FeatureStage | null): void => {
      dispatch(setRowStageOverride({ index, stage }));
    },
    [dispatch]
  );

  const handleResetAllStages = useCallback((): void => {
    dispatch(resetAllStageOverrides());
  }, [dispatch]);

  const handleResetAll = useCallback((): void => {
    dispatch(resetAllFeatureOverrides());
  }, [dispatch]);

  const handleSetVideoDebugOverlay = useCallback(
    (isEnabled: boolean): void => {
      dispatch(setVideoDebugOverlayEnabled(isEnabled));
    },
    [dispatch]
  );

  if (isVisible !== true) {
    return null;
  }

  return (
    <FeatureOverrideScreen
      rows={rows}
      focusedIndex={focusedIndex}
      resetStageIndex={resetStageIndex}
      resetFeatureIndex={resetFeatureIndex}
      videoDebugOverlayIndex={videoDebugOverlayIndex}
      isVideoDebugOverlayEnabled={isVideoDebugOverlayEnabled}
      onFocusControl={handleFocusControl}
      onSelectValue={handleSelectValue}
      onSelectStage={handleSelectStage}
      onResetAllStages={handleResetAllStages}
      onResetAll={handleResetAll}
      onSetVideoDebugOverlay={handleSetVideoDebugOverlay}
    />
  );
};

export default FeatureOverrideContainer;
